import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { patientsAPI, visitsAPI, vitalsAPI, diagnosesAPI, prescriptionsAPI, clinicalNotesAPI, labReportsAPI, filesAPI } from '../lib/api';
import { toast } from 'sonner';
import EditPatientDialog from '../components/EditPatientDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PatientJourneyTab } from '../components/PatientJourneyTab';
import {
  ArrowLeft, Plus, Heart, Thermometer, Activity, FileText, Pill,
  ClipboardList, FlaskConical, Upload, Download, Calendar, Clock, ArrowRightCircle,
  Stethoscope, ChevronDown, ChevronUp, Eye, Star, Pencil,
} from 'lucide-react';
import ReferToIPDDialog from '../components/ReferToIPDDialog';

// =================== Visit Creation Dialog ===================
const CreateVisitDialog = ({ open, onOpenChange, patientId, doctors, onCreated }) => {
  const [form, setForm] = useState({ doctor_id: '', visit_type: 'consultation', visit_date: new Date().toISOString().split('T')[0], visit_time: '', chief_complaint: '' });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.doctor_id || !form.visit_date) { toast.error('Select doctor and date'); return; }
    setLoading(true);
    try {
      await visitsAPI.create({ patient_id: patientId, ...form });
      toast.success('Visit created');
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="create-visit-dialog">
        <DialogHeader><DialogTitle>New Visit</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Doctor *</Label>
            <Select value={form.doctor_id} onValueChange={v => set('doctor_id', v)}>
              <SelectTrigger data-testid="visit-doctor-select"><SelectValue placeholder="Select Doctor" /></SelectTrigger>
              <SelectContent>
                {doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Type</Label>
              <Select value={form.visit_type} onValueChange={v => set('visit_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['consultation', 'follow_up', 'emergency', 'admission'].map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Date *</Label><Input type="date" value={form.visit_date} onChange={e => set('visit_date', e.target.value)} data-testid="visit-date-input" /></div>
          </div>
          <div><Label>Chief Complaint</Label><Textarea value={form.chief_complaint} onChange={e => set('chief_complaint', e.target.value)} rows={2} data-testid="visit-complaint-input" /></div>
          <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="create-visit-btn">{loading ? 'Creating...' : 'Create Visit'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// =================== Vitals Dialog ===================
const RecordVitalsDialog = ({ open, onOpenChange, patientId, visitId, onCreated }) => {
  const [form, setForm] = useState({
    blood_pressure_systolic: '', blood_pressure_diastolic: '', pulse_rate: '', temperature: '',
    respiratory_rate: '', oxygen_saturation: '', weight: '', height: '', blood_sugar: '', notes: '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { patient_id: patientId, visit_id: visitId };
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v !== null) payload[k] = k === 'notes' ? v : Number(v); });
      await vitalsAPI.create(payload);
      toast.success('Vitals recorded');
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="record-vitals-dialog">
        <DialogHeader><DialogTitle>Record Vitals</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>BP Systolic (mmHg)</Label><Input type="number" value={form.blood_pressure_systolic} onChange={e => set('blood_pressure_systolic', e.target.value)} /></div>
            <div><Label>BP Diastolic (mmHg)</Label><Input type="number" value={form.blood_pressure_diastolic} onChange={e => set('blood_pressure_diastolic', e.target.value)} /></div>
            <div><Label>Pulse (bpm)</Label><Input type="number" value={form.pulse_rate} onChange={e => set('pulse_rate', e.target.value)} /></div>
            <div><Label>Temp (C)</Label><Input type="number" step="0.1" value={form.temperature} onChange={e => set('temperature', e.target.value)} /></div>
            <div><Label>Resp Rate</Label><Input type="number" value={form.respiratory_rate} onChange={e => set('respiratory_rate', e.target.value)} /></div>
            <div><Label>SpO2 (%)</Label><Input type="number" value={form.oxygen_saturation} onChange={e => set('oxygen_saturation', e.target.value)} /></div>
            <div><Label>Weight (kg)</Label><Input type="number" step="0.1" value={form.weight} onChange={e => set('weight', e.target.value)} /></div>
            <div><Label>Height (cm)</Label><Input type="number" step="0.1" value={form.height} onChange={e => set('height', e.target.value)} /></div>
            <div><Label>Blood Sugar (mg/dL)</Label><Input type="number" value={form.blood_sugar} onChange={e => set('blood_sugar', e.target.value)} /></div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} /></div>
          <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-vitals-btn">{loading ? 'Saving...' : 'Save Vitals'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// =================== Diagnosis Dialog ===================
const AddDiagnosisDialog = ({ open, onOpenChange, patientId, visitId, onCreated }) => {
  const [form, setForm] = useState({ diagnosis_name: '', diagnosis_type: 'primary', severity: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.diagnosis_name) { toast.error('Diagnosis name required'); return; }
    setLoading(true);
    try {
      await diagnosesAPI.create({ patient_id: patientId, visit_id: visitId, ...form });
      toast.success('Diagnosis added');
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="add-diagnosis-dialog">
        <DialogHeader><DialogTitle>Add Diagnosis</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label>Diagnosis Name *</Label><Input value={form.diagnosis_name} onChange={e => set('diagnosis_name', e.target.value)} data-testid="diagnosis-name-input" /></div>
          <div><Label>Type</Label>
            <Select value={form.diagnosis_type} onValueChange={v => set('diagnosis_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['primary', 'secondary', 'differential'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Severity</Label>
            <Select value={form.severity} onValueChange={v => set('severity', v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {['mild', 'moderate', 'severe'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} /></div>
          <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-diagnosis-btn">{loading ? 'Saving...' : 'Save Diagnosis'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// =================== Prescription Dialog ===================
const AddPrescriptionDialog = ({ open, onOpenChange, patientId, visitId, onCreated }) => {
  const [meds, setMeds] = useState([{ name: '', dosage: '', frequency: '', duration: '', route: 'oral', instructions: '' }]);
  const [instructions, setInstructions] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [loading, setLoading] = useState(false);

  const addMed = () => setMeds(m => [...m, { name: '', dosage: '', frequency: '', duration: '', route: 'oral', instructions: '' }]);
  const updateMed = (i, k, v) => setMeds(m => m.map((med, idx) => idx === i ? { ...med, [k]: v } : med));
  const removeMed = (i) => setMeds(m => m.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validMeds = meds.filter(m => m.name);
    if (!validMeds.length) { toast.error('Add at least one medication'); return; }
    setLoading(true);
    try {
      await prescriptionsAPI.create({ patient_id: patientId, visit_id: visitId, medications: validMeds, instructions, follow_up_date: followUp || null });
      toast.success('Prescription created');
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-prescription-dialog">
        <DialogHeader><DialogTitle>Add Prescription</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {meds.map((med, i) => (
            <div key={i} className="p-3 border rounded-lg space-y-3 bg-slate-50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">Medication {i + 1}</span>
                {meds.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeMed(i)} className="text-red-500 h-7">Remove</Button>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name *</Label><Input value={med.name} onChange={e => updateMed(i, 'name', e.target.value)} data-testid={`med-name-${i}`} /></div>
                <div><Label>Dosage</Label><Input value={med.dosage} onChange={e => updateMed(i, 'dosage', e.target.value)} placeholder="e.g. 500mg" /></div>
                <div><Label>Frequency</Label><Input value={med.frequency} onChange={e => updateMed(i, 'frequency', e.target.value)} placeholder="e.g. Twice daily" /></div>
                <div><Label>Duration</Label><Input value={med.duration} onChange={e => updateMed(i, 'duration', e.target.value)} placeholder="e.g. 7 days" /></div>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addMed}>+ Add Medication</Button>
          <div><Label>General Instructions</Label><Textarea value={instructions} onChange={e => setInstructions(e.target.value)} rows={2} /></div>
          <div><Label>Follow-up Date</Label><Input type="date" value={followUp} onChange={e => setFollowUp(e.target.value)} /></div>
          <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-prescription-btn">{loading ? 'Saving...' : 'Save Prescription'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// =================== Clinical Note Dialog ===================
const AddNoteDialog = ({ open, onOpenChange, patientId, visitId, onCreated }) => {
  const [form, setForm] = useState({ note_type: 'soap', subjective: '', objective: '', assessment: '', plan: '', content: '' });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await clinicalNotesAPI.create({ patient_id: patientId, visit_id: visitId, ...form });
      toast.success('Note added');
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-note-dialog">
        <DialogHeader><DialogTitle>Add Clinical Note</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label>Note Type</Label>
            <Select value={form.note_type} onValueChange={v => set('note_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['soap', 'progress', 'consultation', 'procedure', 'discharge'].map(t => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.note_type === 'soap' ? (
            <>
              <div><Label>Subjective</Label><Textarea value={form.subjective} onChange={e => set('subjective', e.target.value)} rows={2} placeholder="Patient complaints..." /></div>
              <div><Label>Objective</Label><Textarea value={form.objective} onChange={e => set('objective', e.target.value)} rows={2} placeholder="Examination findings..." /></div>
              <div><Label>Assessment</Label><Textarea value={form.assessment} onChange={e => set('assessment', e.target.value)} rows={2} placeholder="Diagnosis/Assessment..." /></div>
              <div><Label>Plan</Label><Textarea value={form.plan} onChange={e => set('plan', e.target.value)} rows={2} placeholder="Treatment plan..." /></div>
            </>
          ) : (
            <div><Label>Content</Label><Textarea value={form.content} onChange={e => set('content', e.target.value)} rows={6} /></div>
          )}
          <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-note-btn">{loading ? 'Saving...' : 'Save Note'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// =================== Lab Report Dialog ===================
const AddLabReportDialog = ({ open, onOpenChange, patientId, visitId, onCreated }) => {
  const [form, setForm] = useState({ test_name: '', test_category: 'blood', result_notes: '', status: 'ordered' });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.test_name) { toast.error('Test name required'); return; }
    setLoading(true);
    try {
      let fileUrl = null;
      if (file) {
        const uploadRes = await filesAPI.upload(file);
        fileUrl = uploadRes.data.id;
      }
      await labReportsAPI.create({
        patient_id: patientId, visit_id: visitId || null,
        ...form, result_file_url: fileUrl,
      });
      toast.success('Lab report created');
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="add-lab-report-dialog">
        <DialogHeader><DialogTitle>Add Lab Report</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label>Test Name *</Label><Input value={form.test_name} onChange={e => set('test_name', e.target.value)} data-testid="lab-test-name-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Category</Label>
              <Select value={form.test_category} onValueChange={v => set('test_category', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['blood', 'urine', 'imaging', 'pathology', 'microbiology', 'other'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v => set('status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['ordered', 'sample_collected', 'processing', 'completed'].map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.result_notes} onChange={e => set('result_notes', e.target.value)} rows={2} /></div>
          <div>
            <Label>Attach Report File</Label>
            <Input type="file" accept=".pdf,.txt,.png,.jpg,.jpeg" onChange={e => setFile(e.target.files[0])} data-testid="lab-report-file-input" className="mt-1" />
            <p className="text-xs text-slate-400 mt-1">PDF, TXT, PNG, JPG (max 10MB)</p>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-lab-report-btn">{loading ? 'Saving...' : 'Save Lab Report'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// =================== Visit EMR Card ===================
const VisitCard = ({ visit, patientId, onRefresh }) => {
  const { user } = useAuth();
  const isDoctor = (user?.role_name || '').toLowerCase() === 'doctor' || (user?.role_code || '').toLowerCase() === 'doctor' || user?.is_super_admin;
  const isLabTech = (user?.role_name || '').toLowerCase().includes('lab') || (user?.role_code || '').toLowerCase().includes('lab');
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showVitals, setShowVitals] = useState(false);
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const [showPrescription, setShowPrescription] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [showLabReport, setShowLabReport] = useState(false);
  const [showReferDialog, setShowReferDialog] = useState(false);

  const guardDoctorAction = (label) => {
    if (isDoctor) return true;
    toast.warning(`Please contact the Doctor to add ${label}. Only doctors can record clinical entries.`);
    return false;
  };
  const guardLabAction = () => {
    if (isDoctor || isLabTech) return true;
    toast.warning('Please contact the Lab Technician to upload reports.');
    return false;
  };

  const loadDetail = async () => {
    if (detail) { setExpanded(!expanded); return; }
    setLoadingDetail(true);
    try {
      const res = await visitsAPI.get(visit.id);
      setDetail(res.data);
      setExpanded(true);
    } catch (e) { toast.error('Failed to load visit details'); }
    finally { setLoadingDetail(false); }
  };

  const refreshDetail = async () => {
    try {
      const res = await visitsAPI.get(visit.id);
      setDetail(res.data);
    } catch {}
    onRefresh?.();
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const res = await filesAPI.download(fileId);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'file';
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const typeColors = { consultation: 'bg-blue-100 text-blue-700', follow_up: 'bg-purple-100 text-purple-700', emergency: 'bg-red-100 text-red-700', admission: 'bg-amber-100 text-amber-700' };
  const statusColors = { scheduled: 'bg-slate-100 text-slate-600', in_progress: 'bg-blue-100 text-blue-700', completed: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-red-100 text-red-600' };

  return (
    <Card className="overflow-hidden" data-testid={`visit-card-${visit.id}`}>
      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors" onClick={loadDetail}>
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
            <Calendar size={18} className="text-emerald-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-900">{visit.visit_date}</span>
              {visit.visit_time && <span className="text-sm text-slate-500"><Clock size={12} className="inline mr-1" />{visit.visit_time}</span>}
            </div>
            <p className="text-sm text-slate-500">{visit.doctor_name || 'Doctor'} {visit.chief_complaint && `- ${visit.chief_complaint}`}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={typeColors[visit.visit_type] || 'bg-slate-100'}>{visit.visit_type?.replace('_', ' ')}</Badge>
          <Badge className={statusColors[visit.status] || 'bg-slate-100'}>{visit.status}</Badge>
          {loadingDetail ? <div className="h-5 w-5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /> : expanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </div>
      </div>

      {expanded && detail && (
        <div className="border-t px-4 pb-4 space-y-4">
          <div className="flex items-center gap-2 pt-3 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => guardDoctorAction('Vitals') && setShowVitals(true)} data-testid="add-vitals-btn"><Heart size={14} className="mr-1" /> Vitals</Button>
            <Button size="sm" variant="outline" onClick={() => guardDoctorAction('a Diagnosis') && setShowDiagnosis(true)} data-testid="add-diagnosis-btn"><Stethoscope size={14} className="mr-1" /> Diagnosis</Button>
            <Button size="sm" variant="outline" onClick={() => guardDoctorAction('a Prescription') && setShowPrescription(true)} data-testid="add-prescription-btn"><Pill size={14} className="mr-1" /> Prescription</Button>
            <Button size="sm" variant="outline" onClick={() => guardDoctorAction('a Clinical Note') && setShowNote(true)} data-testid="add-note-btn"><FileText size={14} className="mr-1" /> Note</Button>
            <Button size="sm" variant="outline" onClick={() => guardLabAction() && setShowLabReport(true)} data-testid="add-lab-report-btn"><FlaskConical size={14} className="mr-1" /> Lab Report</Button>
            <Button size="sm" variant="outline" onClick={() => guardDoctorAction('an IPD Referral') && setShowReferDialog(true)} data-testid="refer-to-ipd-btn" className="border-sky-300 text-sky-700 hover:bg-sky-50">
              <ArrowRightCircle size={14} className="mr-1" /> Refer to IPD
            </Button>
          </div>

          {/* Vitals */}
          {detail.vitals?.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><Heart size={14} className="text-red-500" /> Vitals</h5>
              {detail.vitals.map(v => (
                <div key={v.id} className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 bg-rose-50 rounded-lg text-sm mb-2" data-testid={`vitals-${v.id}`}>
                  {v.blood_pressure_systolic && <div><span className="text-slate-500">BP:</span> <span className="font-medium">{v.blood_pressure_systolic}/{v.blood_pressure_diastolic}</span></div>}
                  {v.pulse_rate && <div><span className="text-slate-500">Pulse:</span> <span className="font-medium">{v.pulse_rate} bpm</span></div>}
                  {v.temperature && <div><span className="text-slate-500">Temp:</span> <span className="font-medium">{v.temperature}°C</span></div>}
                  {v.oxygen_saturation && <div><span className="text-slate-500">SpO2:</span> <span className="font-medium">{v.oxygen_saturation}%</span></div>}
                  {v.weight && <div><span className="text-slate-500">Weight:</span> <span className="font-medium">{v.weight} kg</span></div>}
                  {v.bmi && <div><span className="text-slate-500">BMI:</span> <span className="font-medium">{v.bmi}</span></div>}
                  {v.blood_sugar && <div><span className="text-slate-500">Sugar:</span> <span className="font-medium">{v.blood_sugar} mg/dL</span></div>}
                </div>
              ))}
            </div>
          )}

          {/* Diagnoses */}
          {detail.diagnoses?.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><Stethoscope size={14} className="text-blue-500" /> Diagnoses</h5>
              {detail.diagnoses.map(d => (
                <div key={d.id} className="p-3 bg-blue-50 rounded-lg text-sm mb-2" data-testid={`diagnosis-${d.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{d.diagnosis_name}</span>
                    <Badge className={d.diagnosis_type === 'primary' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>{d.diagnosis_type}</Badge>
                    {d.severity && <Badge className={d.severity === 'severe' ? 'bg-red-100 text-red-700' : d.severity === 'moderate' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>{d.severity}</Badge>}
                  </div>
                  {d.notes && <p className="text-slate-500 mt-1">{d.notes}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Prescriptions */}
          {detail.prescriptions?.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><Pill size={14} className="text-green-500" /> Prescriptions</h5>
              {detail.prescriptions.map(p => (
                <div key={p.id} className="p-3 bg-green-50 rounded-lg text-sm mb-2" data-testid={`prescription-${p.id}`}>
                  {p.medications?.map((m, i) => (
                    <div key={i} className="flex items-center gap-3 py-1">
                      <span className="font-medium">{m.name}</span>
                      {m.dosage && <span className="text-slate-500">{m.dosage}</span>}
                      {m.frequency && <span className="text-slate-500">{m.frequency}</span>}
                      {m.duration && <Badge variant="outline" className="text-xs">{m.duration}</Badge>}
                    </div>
                  ))}
                  {p.instructions && <p className="text-slate-500 mt-1 border-t pt-1">{p.instructions}</p>}
                  {p.follow_up_date && <p className="text-emerald-600 text-xs mt-1">Follow-up: {p.follow_up_date}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Clinical Notes */}
          {detail.clinical_notes?.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><FileText size={14} className="text-purple-500" /> Clinical Notes</h5>
              {detail.clinical_notes.map(n => (
                <div key={n.id} className="p-3 bg-purple-50 rounded-lg text-sm mb-2" data-testid={`note-${n.id}`}>
                  <Badge className="bg-purple-100 text-purple-700 mb-2">{n.note_type?.toUpperCase()}</Badge>
                  {n.note_type === 'soap' ? (
                    <div className="space-y-1">
                      {n.subjective && <p><span className="font-medium">S:</span> {n.subjective}</p>}
                      {n.objective && <p><span className="font-medium">O:</span> {n.objective}</p>}
                      {n.assessment && <p><span className="font-medium">A:</span> {n.assessment}</p>}
                      {n.plan && <p><span className="font-medium">P:</span> {n.plan}</p>}
                    </div>
                  ) : (
                    <p className="text-slate-600">{n.content}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Lab Reports */}
          {detail.lab_reports?.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><FlaskConical size={14} className="text-amber-500" /> Lab Reports</h5>
              {detail.lab_reports.map(r => (
                <div key={r.id} className="p-3 bg-amber-50 rounded-lg text-sm mb-2 flex items-center justify-between" data-testid={`lab-report-${r.id}`}>
                  <div>
                    <span className="font-medium">{r.test_name}</span>
                    <Badge className="ml-2 bg-slate-100 text-slate-600">{r.test_category}</Badge>
                    <Badge className={`ml-2 ${r.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.status?.replace('_', ' ')}</Badge>
                    {r.result_notes && <p className="text-slate-500 mt-1">{r.result_notes}</p>}
                  </div>
                  {r.result_file_url && (
                    <Button size="sm" variant="ghost" onClick={() => handleDownload(r.result_file_url, `${r.test_name}.pdf`)} data-testid={`download-report-${r.id}`}>
                      <Download size={14} className="mr-1" /> Download
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          <RecordVitalsDialog open={showVitals} onOpenChange={setShowVitals} patientId={patientId} visitId={visit.id} onCreated={refreshDetail} />
          <AddDiagnosisDialog open={showDiagnosis} onOpenChange={setShowDiagnosis} patientId={patientId} visitId={visit.id} onCreated={refreshDetail} />
          <AddPrescriptionDialog open={showPrescription} onOpenChange={setShowPrescription} patientId={patientId} visitId={visit.id} onCreated={refreshDetail} />
          <AddNoteDialog open={showNote} onOpenChange={setShowNote} patientId={patientId} visitId={visit.id} onCreated={refreshDetail} />
          <AddLabReportDialog open={showLabReport} onOpenChange={setShowLabReport} patientId={patientId} visitId={visit.id} onCreated={refreshDetail} />
          <ReferToIPDDialog
            open={showReferDialog}
            onOpenChange={setShowReferDialog}
            patient={visit.patient || { id: patientId, first_name: visit.patient_name?.split(' ')[0], last_name: visit.patient_name?.split(' ').slice(1).join(' '), uhid: visit.patient_uhid }}
            sourceVisitId={visit.id}
            defaultDiagnosis={detail?.diagnoses?.[0]?.summary || detail?.diagnoses?.[0]?.diagnosis || ''}
            onCreated={() => { toast.success('Referral sent — IPD desk will pick up.'); }}
          />
        </div>
      )}
    </Card>
  );
};

// =================== Main Patient Detail Page ===================
export const PatientDetailPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRoleCode = (user?.role_code || user?.role_name || '').toLowerCase().replace(/\s+/g, '_');
  const canManageVip = user?.is_super_admin || ['reception', 'front_desk', 'it_admin', 'hospital_admin'].includes(userRoleCode);
  const canEditPatient = user?.is_super_admin || ['reception', 'front_desk', 'billing_staff', 'hospital_admin', 'it_admin', 'hr', 'doctor', 'nurse'].includes(userRoleCode);
  const [patient, setPatient] = useState(null);
  const [history, setHistory] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateVisit, setShowCreateVisit] = useState(false);
  const [showLabReport, setShowLabReport] = useState(false);
  const [showVipDialog, setShowVipDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [vipForm, setVipForm] = useState({ is_vip: false, vip_tier: '', vip_notes: '' });
  const [savingVip, setSavingVip] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, hRes, dRes] = await Promise.all([
        patientsAPI.get(patientId),
        patientsAPI.getHistory(patientId),
        patientsAPI.getDoctors(),
      ]);
      setPatient(pRes.data);
      setVipForm({
        is_vip: !!pRes.data?.is_vip,
        vip_tier: pRes.data?.vip_tier || '',
        vip_notes: pRes.data?.vip_notes || '',
      });
      setHistory(hRes.data);
      setDoctors(dRes.data);
    } catch (e) {
      toast.error('Failed to load patient');
      navigate('/patients');
    } finally {
      setLoading(false);
    }
  }, [patientId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400">Loading...</div>;
  if (!patient) return null;

  const age = patient.date_of_birth ? Math.floor((new Date() - new Date(patient.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000)) : null;

  return (
    <div className="space-y-6" data-testid="patient-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/patients')} data-testid="back-to-patients-btn">
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
      </div>

      {/* Patient Header */}
      <Card className={patient.is_vip ? 'border-amber-300 ring-1 ring-amber-200' : ''}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-5">
              <div className={`relative h-16 w-16 rounded-full flex items-center justify-center font-bold text-xl ${patient.is_vip ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                {patient.first_name?.[0]}{patient.last_name?.[0]}
                {patient.is_vip && <Star size={16} className="absolute -bottom-0.5 -right-0.5 fill-amber-400 text-amber-500 bg-white rounded-full p-0.5 border border-amber-200" />}
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-testid="patient-name">
                  {patient.title ? `${patient.title} ` : ''}{patient.first_name} {patient.last_name}
                  {patient.is_vip && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200" data-testid="patient-vip-badge">
                      <Star size={10} className="fill-amber-500 text-amber-500" /> VIP{patient.vip_tier ? ` · ${patient.vip_tier}` : ''}
                    </span>
                  )}
                </h2>
                <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                  <span className="font-mono font-medium text-emerald-700" data-testid="patient-id-display">{patient.patient_id}</span>
                  {age !== null && <span>{age} yrs</span>}
                  <span className="capitalize">{patient.gender}</span>
                  {patient.blood_group && <Badge variant="outline">{patient.blood_group}</Badge>}
                </div>
                <p className="text-sm text-slate-500 mt-1">{patient.phone} {patient.email && `| ${patient.email}`}</p>
                {patient.is_vip && patient.vip_notes && (
                  <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mt-2 inline-block">Note: {patient.vip_notes}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canEditPatient && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEditDialog(true)}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="edit-patient-btn"
                >
                  <Pencil size={14} className="mr-1" />
                  Edit Profile
                </Button>
              )}
              {canManageVip && (
                <Button
                  variant={patient.is_vip ? 'outline' : 'ghost'}
                  size="sm"
                  className={patient.is_vip ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'text-slate-500 hover:text-amber-600 hover:bg-amber-50'}
                  onClick={() => setShowVipDialog(true)}
                  data-testid="vip-toggle-btn"
                >
                  <Star size={14} className={`mr-1 ${patient.is_vip ? 'fill-amber-400 text-amber-500' : ''}`} />
                  {patient.is_vip ? 'Manage VIP' : 'Mark as VIP'}
                </Button>
              )}
              <Badge className={patient.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                {patient.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="journey" data-testid="tab-journey">Journey</TabsTrigger>
          <TabsTrigger value="visits" data-testid="tab-visits">Visits ({history?.visits?.length || 0})</TabsTrigger>
          <TabsTrigger value="lab-reports" data-testid="tab-lab-reports">Lab Reports ({history?.lab_reports?.length || 0})</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Contact & Address</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                {patient.address && <p><span className="text-slate-500">Address:</span> {patient.address}</p>}
                {patient.city && <p><span className="text-slate-500">City:</span> {patient.city}{patient.state ? `, ${patient.state}` : ''}{patient.pincode ? ` - ${patient.pincode}` : ''}</p>}
                {patient.alternate_phone && <p><span className="text-slate-500">Alt Phone:</span> {patient.alternate_phone}</p>}
                {patient.marital_status && <p><span className="text-slate-500">Marital:</span> <span className="capitalize">{patient.marital_status}</span></p>}
                {patient.occupation && <p><span className="text-slate-500">Occupation:</span> {patient.occupation}</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Emergency Contact</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                {patient.emergency_contact_name ? (
                  <>
                    <p><span className="text-slate-500">Name:</span> {patient.emergency_contact_name}</p>
                    {patient.emergency_contact_phone && <p><span className="text-slate-500">Phone:</span> {patient.emergency_contact_phone}</p>}
                    {patient.emergency_contact_relation && <p><span className="text-slate-500">Relation:</span> {patient.emergency_contact_relation}</p>}
                  </>
                ) : <p className="text-slate-400">No emergency contact</p>}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base text-red-600">Allergies</CardTitle></CardHeader>
              <CardContent>
                {patient.allergies?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">{patient.allergies.map((a, i) => <Badge key={i} variant="outline" className="border-red-200 text-red-700 bg-red-50">{a}</Badge>)}</div>
                ) : <p className="text-sm text-slate-400">None recorded</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base text-amber-600">Chronic Conditions</CardTitle></CardHeader>
              <CardContent>
                {patient.chronic_conditions?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">{patient.chronic_conditions.map((c, i) => <Badge key={i} variant="outline" className="border-amber-200 text-amber-700 bg-amber-50">{c}</Badge>)}</div>
                ) : <p className="text-sm text-slate-400">None recorded</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base text-blue-600">Current Medications</CardTitle></CardHeader>
              <CardContent>
                {patient.current_medications?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">{patient.current_medications.map((m, i) => <Badge key={i} variant="outline" className="border-blue-200 text-blue-700 bg-blue-50">{m}</Badge>)}</div>
                ) : <p className="text-sm text-slate-400">None recorded</p>}
              </CardContent>
            </Card>
          </div>

          {/* Recent Vitals */}
          {history?.vitals_history?.length > 0 && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Latest Vitals</CardTitle></CardHeader>
              <CardContent>
                {(() => {
                  const v = history.vitals_history[0];
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 text-sm">
                      {v.blood_pressure_systolic && <div className="p-3 bg-rose-50 rounded-lg text-center"><p className="text-slate-500 text-xs">Blood Pressure</p><p className="font-semibold">{v.blood_pressure_systolic}/{v.blood_pressure_diastolic}</p></div>}
                      {v.pulse_rate && <div className="p-3 bg-red-50 rounded-lg text-center"><p className="text-slate-500 text-xs">Pulse</p><p className="font-semibold">{v.pulse_rate} bpm</p></div>}
                      {v.temperature && <div className="p-3 bg-amber-50 rounded-lg text-center"><p className="text-slate-500 text-xs">Temp</p><p className="font-semibold">{v.temperature}°C</p></div>}
                      {v.oxygen_saturation && <div className="p-3 bg-blue-50 rounded-lg text-center"><p className="text-slate-500 text-xs">SpO2</p><p className="font-semibold">{v.oxygen_saturation}%</p></div>}
                      {v.weight && <div className="p-3 bg-green-50 rounded-lg text-center"><p className="text-slate-500 text-xs">Weight</p><p className="font-semibold">{v.weight} kg</p></div>}
                      {v.height && <div className="p-3 bg-emerald-50 rounded-lg text-center"><p className="text-slate-500 text-xs">Height</p><p className="font-semibold">{v.height} cm</p></div>}
                      {v.bmi && <div className="p-3 bg-purple-50 rounded-lg text-center"><p className="text-slate-500 text-xs">BMI</p><p className="font-semibold">{v.bmi}</p></div>}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Journey Tab (v36) — unified chronological timeline across 9+ collections */}
        <TabsContent value="journey" className="space-y-4">
          <PatientJourneyTab patientId={patient.id} />
        </TabsContent>

        {/* Visits Tab */}
        <TabsContent value="visits" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Visit History</h3>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowCreateVisit(true)} data-testid="create-visit-btn">
              <Plus size={16} className="mr-1" /> New Visit
            </Button>
          </div>
          {history?.visits?.length > 0 ? (
            <div className="space-y-3">
              {history.visits.map(v => <VisitCard key={v.id} visit={v} patientId={patientId} onRefresh={load} />)}
            </div>
          ) : (
            <Card><CardContent className="flex flex-col items-center justify-center py-12 text-slate-400"><Calendar size={40} className="mb-3 opacity-40" /><p>No visits yet</p></CardContent></Card>
          )}
        </TabsContent>

        {/* Lab Reports Tab */}
        <TabsContent value="lab-reports" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Lab Reports</h3>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowLabReport(true)} data-testid="add-standalone-lab-report-btn">
              <Plus size={16} className="mr-1" /> Add Report
            </Button>
          </div>
          {history?.lab_reports?.length > 0 ? (
            <div className="space-y-3">
              {history.lab_reports.map(r => {
                const handleDownload = async () => {
                  if (!r.result_file_url) return;
                  try {
                    const res = await filesAPI.download(r.result_file_url);
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${r.test_name}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch { toast.error('Download failed'); }
                };
                return (
                  <Card key={r.id}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <FlaskConical size={16} className="text-amber-500" />
                          <span className="font-medium" data-testid={`lab-report-name-${r.id}`}>{r.test_name}</span>
                          <Badge className="bg-slate-100 text-slate-600">{r.test_category}</Badge>
                          <Badge className={r.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>{r.status?.replace('_', ' ')}</Badge>
                        </div>
                        {r.result_notes && <p className="text-sm text-slate-500 mt-1">{r.result_notes}</p>}
                        <p className="text-xs text-slate-400 mt-1">{r.ordered_at ? new Date(r.ordered_at).toLocaleDateString() : ''}</p>
                      </div>
                      {r.result_file_url && (
                        <Button size="sm" variant="outline" onClick={handleDownload} data-testid={`download-lab-${r.id}`}>
                          <Download size={14} className="mr-1" /> Download
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card><CardContent className="flex flex-col items-center justify-center py-12 text-slate-400"><FlaskConical size={40} className="mb-3 opacity-40" /><p>No lab reports yet</p></CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      <CreateVisitDialog open={showCreateVisit} onOpenChange={setShowCreateVisit} patientId={patientId} doctors={doctors} onCreated={load} />
      <AddLabReportDialog open={showLabReport} onOpenChange={setShowLabReport} patientId={patientId} visitId={null} onCreated={load} />

      {/* VIP Toggle Dialog */}
      <Dialog open={showVipDialog} onOpenChange={setShowVipDialog}>
        <DialogContent className="sm:max-w-md" data-testid="vip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Star className="h-5 w-5 fill-amber-400 text-amber-500" /> VIP Status</DialogTitle>
            <DialogDescription>
              Marking this patient as VIP flags them across queues, appointments, billing and pharmacy so the whole team can offer priority handling.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <label className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer">
              <input type="checkbox" checked={vipForm.is_vip} onChange={e => setVipForm(f => ({ ...f, is_vip: e.target.checked }))}
                className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500" data-testid="vip-checkbox" />
              <span className="text-sm font-semibold text-amber-900">Treat this patient as VIP</span>
            </label>
            {vipForm.is_vip && (
              <>
                <div>
                  <Label className="text-xs">VIP Tier</Label>
                  <Select value={vipForm.vip_tier} onValueChange={v => setVipForm(f => ({ ...f, vip_tier: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select tier (optional)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="platinum">Platinum</SelectItem>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                      <SelectItem value="corporate">Corporate Account</SelectItem>
                      <SelectItem value="celebrity">Celebrity / Public Figure</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">VIP Notes</Label>
                  <Input value={vipForm.vip_notes} onChange={e => setVipForm(f => ({ ...f, vip_notes: e.target.value }))} placeholder="Brief handling note" />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowVipDialog(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                setSavingVip(true);
                try {
                  await patientsAPI.toggleVip(patient.id, vipForm);
                  toast.success(vipForm.is_vip ? 'Marked as VIP' : 'Removed VIP status');
                  setShowVipDialog(false);
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to update VIP');
                } finally { setSavingVip(false); }
              }}
              disabled={savingVip}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="vip-save-btn"
            >
              {savingVip ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Patient Dialog */}
      {showEditDialog && (
        <EditPatientDialog
          patient={patient}
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default PatientDetailPage;
